import { cn, NoddiIcon } from 'noddi-ui-common';
import { useFullScreenBodyLock } from 'noddi-ui-web';
import type { WheelField } from 'noddi-util';
import { useState } from 'react';
import { TireDimensionLabel } from '../Tire/TireDimensionLabel';
import { PhotoTile } from './PhotoGrid';
import { PhotoLightbox } from './PhotoLightbox';

/** The four wheels, named as `noddi-util` names them wherever a wheel is keyed. */
export type WheelPhotoPosition = WheelField;

export interface WheelPhoto {
  position: WheelPhotoPosition;
  label: string;
  imageUrl: string | null;
}

interface Props {
  wheels: WheelPhoto[];
  tireDimensions: {
    all?: string;
    front?: string;
    rear?: string;
  };
  seasonText?: string;
  noPhotoLabel: string;
  /** 'compact' is one row of small thumbnails, for a table cell; the lightbox is the same. */
  size?: 'default' | 'compact';
  /** Overrides the default grid's own inset, for a host that already pads its content. */
  className?: string;
}

const POSITION_ORDER: Record<WheelPhotoPosition, number> = {
  frontLeft: 0,
  frontRight: 1,
  rearLeft: 2,
  rearRight: 3
};

const POSITION_SHORT_LABEL: Record<WheelPhotoPosition, string> = {
  frontLeft: 'FL',
  frontRight: 'FR',
  rearLeft: 'RL',
  rearRight: 'RR'
};

const isFrontPosition = (position: WheelPhotoPosition) => position === 'frontLeft' || position === 'frontRight';

export const WheelPhotoGallery = ({
  wheels,
  tireDimensions,
  seasonText,
  noPhotoLabel,
  size = 'default',
  className
}: Props) => {
  const { isFullScreen, setIsFullScreen, ref } = useFullScreenBodyLock();
  const [activePosition, setActivePosition] = useState<WheelPhotoPosition | null>(null);

  const orderedWheels = [...wheels].sort((a, b) => POSITION_ORDER[a.position] - POSITION_ORDER[b.position]);
  const photosWithImages = orderedWheels.filter(
    (wheel): wheel is WheelPhoto & { imageUrl: string } => !!wheel.imageUrl
  );
  const dimensionsFor = (position: WheelPhotoPosition) =>
    isFrontPosition(position)
      ? (tireDimensions.front ?? tireDimensions.all)
      : (tireDimensions.rear ?? tireDimensions.all);

  const activeIndex = photosWithImages.findIndex((wheel) => wheel.position === activePosition);
  const activeWheel = photosWithImages[activeIndex];
  const activeDimension = activeWheel ? dimensionsFor(activeWheel.position) : undefined;

  return (
    <div ref={ref} className={cn(size === 'compact' ? 'flex gap-1.5' : 'grid grid-cols-2 gap-2 px-5 pb-5', className)}>
      {orderedWheels.map((wheel) => {
        const openPhoto = () => {
          setActivePosition(wheel.position);
          setIsFullScreen(true);
        };
        if (size === 'compact') {
          return (
            <CompactTile
              key={wheel.position}
              imageUrl={wheel.imageUrl}
              label={wheel.label}
              shortLabel={POSITION_SHORT_LABEL[wheel.position]}
              noPhotoLabel={noPhotoLabel}
              onClick={openPhoto}
            />
          );
        }
        return wheel.imageUrl ? (
          <PhotoTile key={wheel.position} imageUrl={wheel.imageUrl} label={wheel.label} onClick={openPhoto} />
        ) : (
          <EmptyTile key={wheel.position} label={wheel.label} noPhotoLabel={noPhotoLabel} />
        );
      })}

      {isFullScreen && activeWheel && (
        <PhotoLightbox
          photos={photosWithImages}
          index={activeIndex}
          onIndexChange={(index) => setActivePosition(photosWithImages[index]!.position)}
          onClose={() => setIsFullScreen(false)}
          details={
            activeDimension || seasonText ? (
              <>
                {activeDimension && <TireDimensionLabel value={activeDimension} />}
                {activeDimension && seasonText ? <span aria-hidden>·</span> : null}
                {seasonText}
              </>
            ) : undefined
          }
        />
      )}
    </div>
  );
};

const CompactTile = ({
  imageUrl,
  label,
  shortLabel,
  noPhotoLabel,
  onClick
}: {
  imageUrl: string | null;
  label: string;
  shortLabel: string;
  noPhotoLabel: string;
  onClick: () => void;
}) =>
  imageUrl ? (
    <button
      type='button'
      onClick={onClick}
      aria-label={label}
      className='relative size-11 shrink-0 cursor-pointer overflow-hidden rounded-md bg-lightPurple ring-secondary transition-shadow hover:ring-2'
    >
      <img src={imageUrl} alt={label} className='size-full object-cover' />
      <span className='absolute bottom-0.5 left-0.5 rounded-sm bg-black/50 px-1 text-[10px] font-semibold text-white'>
        {shortLabel}
      </span>
    </button>
  ) : (
    <span
      title={`${label}: ${noPhotoLabel}`}
      className='flex size-11 shrink-0 items-center justify-center rounded-md border border-darkPurple30 bg-card-light-background text-darkPurple50'
    >
      <NoddiIcon name='CameraOff' size={14} />
      <span className='sr-only'>{`${label}: ${noPhotoLabel}`}</span>
    </span>
  );

const EmptyTile = ({ label, noPhotoLabel }: { label: string; noPhotoLabel: string }) => (
  <div className='relative flex aspect-[4/3] flex-col items-center justify-center gap-1.5 rounded-lg border border-darkPurple30 bg-card-light-background text-darkPurple50'>
    <span className='absolute top-1.5 left-1.5 rounded-full bg-greyMuted/15 px-2 py-0.5 text-[10px] font-semibold text-darkPurple60'>
      {label}
    </span>
    <NoddiIcon name='CameraOff' size={22} strokeWidth={1.6} />
    <span className='text-[10px] font-semibold'>{noPhotoLabel}</span>
  </div>
);
